import React, { useEffect, useState, useCallback, useRef } from "react";
import { Upload } from "lucide-react";
import SingleAudioEditor from "./SingleAudioEditor";
import { AudioEditUtils, AudioSelection, useAudioEdit } from "./useAudioEdit";
import "./MultiAudioEditor.css";

interface MultiAudioEditorProps {
  audioUrl: string;
  audioUrl2: string;
  onMergeComplete?: (mergedBlob: Blob) => Promise<void>; // Add new prop for handling merged file
  onClose?: () => void; // Add new prop for handling merged file
}

const MultiAudioEditor: React.FC<MultiAudioEditorProps> = ({
  audioUrl,
  audioUrl2,
  onMergeComplete,
  onClose,
}) => {
  const [audioContext] = useState(() => new AudioContext());
  const [buffer1, setBuffer1] = useState<AudioBuffer | null>(null);
  const [buffer2, setBuffer2] = useState<AudioBuffer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gainNode1] = useState(() => audioContext.createGain());
  const [gainNode2] = useState(() => audioContext.createGain());
  const [timeOffset1, setTimeOffset1] = useState(0);
  const [timeOffset2, setTimeOffset2] = useState(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const sourceRef1 = useRef<AudioBufferSourceNode | null>(null);
  const sourceRef2 = useRef<AudioBufferSourceNode | null>(null);
  const animationFrameRef = useRef<number>();

  const [zoom, setZoom] = useState(1); // 1이 기본 크기 (100%)
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 32;

  const { addToHistory } = useAudioEdit(audioContext);

  const handleZoomChange = useCallback((delta: number) => {
    setZoom((prev) => {
      const zoomDelta = delta * (prev / 10);
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + zoomDelta));
      return Number(newZoom.toFixed(2));
    });
  }, []);

  // 마스터 게인 노드 생성 및 연결
  const [masterGainNode] = useState(() => {
    const node = audioContext.createGain();
    node.connect(audioContext.destination);
    return node;
  });

  useEffect(() => {
    gainNode1.connect(masterGainNode);
    gainNode2.connect(masterGainNode);

    return () => {
      gainNode1.disconnect();
      gainNode2.disconnect();
      masterGainNode.disconnect();
    };
  }, [gainNode1, gainNode2, masterGainNode]);

  // 오디오 파일 로드
  useEffect(() => {
    const loadAudio = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // 첫 번째 오디오 로드
        const response1 = await fetch(audioUrl);
        const arrayBuffer1 = await response1.arrayBuffer();
        const audioBuffer1 = await audioContext.decodeAudioData(arrayBuffer1);
        setBuffer1(audioBuffer1);

        // 두 번째 오디오 로드
        const response2 = await fetch(audioUrl2);
        const arrayBuffer2 = await response2.arrayBuffer();
        const audioBuffer2 = await audioContext.decodeAudioData(arrayBuffer2);
        setBuffer2(audioBuffer2);
      } catch (err) {
        console.error("Failed to load audio:", err);
        setError("Failed to load one or more audio files");
      } finally {
        setIsLoading(false);
      }
    };

    loadAudio();
  }, [audioUrl, audioUrl2, audioContext]);

  const stopAllSources = useCallback(() => {
    if (sourceRef1.current) {
      sourceRef1.current.stop();
      sourceRef1.current.disconnect();
    }
    if (sourceRef2.current) {
      sourceRef2.current.stop();
      sourceRef2.current.disconnect();
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  const play = useCallback(() => {
    if (!buffer1 || !buffer2 || isPlaying) return;

    const startTime = audioContext.currentTime;
    const startOffset = currentTime;

    const source1 = audioContext.createBufferSource();
    source1.buffer = buffer1;
    source1.connect(gainNode1);
    sourceRef1.current = source1;

    // 두 번째 트랙 설정
    const source2 = audioContext.createBufferSource();
    source2.buffer = buffer2;
    source2.connect(gainNode2);
    sourceRef2.current = source2;

    // 오프셋을 고려한 시작 시점 계산
    const offset1 = Math.max(0, startOffset - timeOffset1);
    const offset2 = Math.max(0, startOffset - timeOffset2);

    // 각 트랙의 시작 시점 조정
    source1.start(0, offset1);
    source2.start(0, offset2);
    setIsPlaying(true);

    const updatePlayhead = () => {
      const elapsedTime = audioContext.currentTime - startTime + startOffset;
      const maxDuration = Math.max(
        buffer1.duration + Math.abs(timeOffset1),
        buffer2.duration + Math.abs(timeOffset2)
      );

      if (elapsedTime >= maxDuration) {
        setIsPlaying(false);
        setCurrentTime(0);
        stopAllSources();
        return;
      }

      setCurrentTime(elapsedTime);
      animationFrameRef.current = requestAnimationFrame(updatePlayhead);
    };

    animationFrameRef.current = requestAnimationFrame(updatePlayhead);
  }, [
    buffer1,
    buffer2,
    audioContext,
    currentTime,
    gainNode1,
    gainNode2,
    isPlaying,
    timeOffset1,
    timeOffset2,
    stopAllSources,
  ]);

  const pause = useCallback(() => {
    if (!isPlaying) return;
    setIsPlaying(false);
    stopAllSources();
  }, [isPlaying, stopAllSources]);

  const stop = useCallback(() => {
    stopAllSources();
    setIsPlaying(false);
    setCurrentTime(0);
  }, [stopAllSources]);

  const handleSeek = useCallback(
    (time: number) => {
      setCurrentTime(time);
      if (isPlaying) {
        stopAllSources();
        setIsPlaying(false);
        setTimeout(() => play(), 0);
      }
    },
    [isPlaying, play, stopAllSources]
  );

  const handleSynchronizedDelete = useCallback(
    async (selection: AudioSelection) => {
      if (!buffer1 || !buffer2 || !selection.start || !selection.end) return;

      const wasPlaying = isPlaying;
      if (wasPlaying) {
        pause();
      }

      try {
        // 두 버퍼 모두에 대해 동일한 구간 삭제
        const newBuffer1 = await AudioEditUtils.deleteSection(
          buffer1,
          selection.start,
          selection.end,
          audioContext
        );
        const newBuffer2 = await AudioEditUtils.deleteSection(
          buffer2,
          selection.start,
          selection.end,
          audioContext
        );

        addToHistory(newBuffer1, { start: null, end: null });
        setBuffer1(newBuffer1);
        setBuffer2(newBuffer2);

        // 현재 재생 시점 조정
        const newTime = Math.min(currentTime, newBuffer1.duration);
        setCurrentTime(newTime);

        if (wasPlaying) {
          setTimeout(() => play(), 0);
        }
      } catch (err) {
        console.error("Failed to delete section:", err);
        setError("Failed to delete section from both tracks");
      }
    },
    [buffer1, buffer2, audioContext, isPlaying, pause, play, currentTime]
  );

  const handleMergedExport = useCallback(async () => {
    if (!buffer1 || !buffer2) return;

    try {
      // MR의 순수 길이만큼만 계산 (모든 offset 고려)
      const totalDuration = buffer2.duration - Math.abs(timeOffset2);
      const sampleRate = audioContext.sampleRate;
      const totalSamples = Math.ceil(totalDuration * sampleRate);

      const mergedBuffer = audioContext.createBuffer(
        2,
        totalSamples,
        sampleRate
      );

      for (let channel = 0; channel < 2; channel++) {
        const outputData = mergedBuffer.getChannelData(channel);
        const buffer1Data = buffer1.getChannelData(
          Math.min(channel, buffer1.numberOfChannels - 1)
        );
        const buffer2Data = buffer2.getChannelData(
          Math.min(channel, buffer2.numberOfChannels - 1)
        );

        const gain1 = gainNode1.gain.value;
        const gain2 = gainNode2.gain.value;

        // offset 조정 (MR의 offset을 기준으로)
        const adjustedOffset1 = timeOffset1 - timeOffset2;

        const offset1Samples = Math.floor(adjustedOffset1 * sampleRate);
        const mrStartSample = Math.abs(timeOffset2) * sampleRate;

        // 두 트랙 믹싱
        for (let i = 0; i < totalSamples; i++) {
          let sample1 = 0;
          let sample2 = 0;

          // MR 트랙 (offset만큼 건너뛴 후부터 시작)
          const sample2Index = i + mrStartSample;
          if (sample2Index >= 0 && sample2Index < buffer2Data.length) {
            sample2 = buffer2Data[sample2Index] * gain2;
          }

          // 보컬 트랙 (MR 기준으로 상대적 위치 계산)
          const sample1Index = i - offset1Samples;
          if (sample1Index >= 0 && sample1Index < buffer1Data.length) {
            sample1 = buffer1Data[sample1Index] * gain1;
          }

          outputData[i] = sample1 + sample2;
        }
      }

      // 클리핑 방지를 위한 정규화
      const normalizedBuffer = await AudioEditUtils.normalizeAudio(
        mergedBuffer
      );
      const blob = await AudioEditUtils.audioBufferToMP3(normalizedBuffer);

      if (onMergeComplete) {
        await onMergeComplete(blob);
      }

      // const url = URL.createObjectURL(blob);
      // const a = document.createElement("a");
      // a.href = url;
      // a.download = "merged-audio.mp3";
      // document.body.appendChild(a);
      // a.click();
      // document.body.removeChild(a);
      // URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export merged audio:", err);
      setError("Failed to export merged audio");
    }
  }, [
    buffer1,
    buffer2,
    audioContext,
    gainNode1,
    gainNode2,
    timeOffset1,
    timeOffset2,
    onMergeComplete,
  ]);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, pause, play]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // 현재 재생 중이거나 오디오가 로드된 상태라면 스페이스바 이벤트를 가로챔
      if ((buffer1 && buffer2) || isPlaying) {
        console.log("Key pressed: ", event.code);
        if (event.code === "Space") {
          event.preventDefault(); // 스크롤 방지
          togglePlayPause();
        }
      }
      if (event.code === "Escape" && onClose) {
        event.preventDefault();
        onClose();
      }
    };

    // 이벤트 리스너 등록
    window.addEventListener("keydown", handleKeyPress);

    // 클린업 함수
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [buffer1, buffer2, isPlaying, togglePlayPause]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!buffer1 || !buffer2) return;

      // Case 1: 트랙패드/마우스 핀치 줌 (Windows/Mac 모두)
      if (e.ctrlKey) {
        e.preventDefault();
        const zoomDelta = -e.deltaY * 0.1;
        handleZoomChange(zoomDelta);
      }
      // Case 2: Shift + Alt + 휠 (기존 방식)
      else if (e.shiftKey && e.altKey) {
        e.preventDefault();
        const delta = e.deltaY !== 0 ? e.deltaY : -e.deltaX;
        const zoomDelta = delta * -0.005;
        handleZoomChange(zoomDelta);
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("mousewheel", handleWheel as any, {
      passive: false,
    });

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("mousewheel", handleWheel as any);
    };
  }, [handleZoomChange, buffer1, buffer2]);

  const getFilenameFromUrl = (url: string) => {
    const urlParts = url.split("/");
    return urlParts[urlParts.length - 1];
  };
  const handleDownloadAll = async () => {
    try {
      setIsLoading(true);

      // Download vocal file
      const vocalLink = document.createElement("a");
      vocalLink.href = audioUrl;
      vocalLink.download = getFilenameFromUrl(audioUrl);
      document.body.appendChild(vocalLink);
      vocalLink.click();
      document.body.removeChild(vocalLink);

      // Small delay between downloads
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Download MR file
      const mrLink = document.createElement("a");
      mrLink.href = audioUrl2;
      mrLink.download = getFilenameFromUrl(audioUrl2);
      document.body.appendChild(mrLink);
      mrLink.click();
      document.body.removeChild(mrLink);
    } catch (err) {
      console.error("Failed to download files:", err);
      setError("Failed to download audio files");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          padding: 24,
          gap: 12,
          fontFamily: "monospace",
        }}
      >
        <button
          onClick={handleMergedExport}
          disabled={!buffer1 || !buffer2}
          className="btn btn-merge"
        >
          완료
        </button>
        {onClose && (
          <button onClick={onClose} className="btn btn-close">
            닫기
          </button>
        )}
        <button
          onClick={handleDownloadAll}
          disabled={!buffer1 || !buffer2 || isLoading}
          className="btn"
        >
          {isLoading ? "다운로드중..." : "오디오파일 다운로드"}
        </button>

        {isLoading && <div className="loading">Loading audio files...</div>}
        {error && <div className="error">{error}</div>}
        <button
          onClick={() => handleZoomChange(zoom - 0.5)}
          disabled={zoom <= 1}
          className="btn"
        >
          Zoom Out
        </button>

        <span style={{ color: "white", textAlign: "center" }}>
          {(zoom * 100).toFixed(0)}%
        </span>
        <button
          onClick={() => handleZoomChange(zoom + 0.5)}
          disabled={zoom >= 10}
          className="btn"
        >
          Zoom In
        </button>
      </div>
      <div
        style={{
          width: "100%",
          overflow: "scroll",
          position: "relative" as const,
        }}
      >
        <div
          style={{
            minWidth: "100%",
            width: `${100 * zoom}%`,
            overflow: "visible",
            // position: "relative",
          }}
        >
          {buffer1 && (
            <SingleAudioEditor
              audioBuffer={buffer1}
              setAudioBuffer={setBuffer1}
              audioContext={audioContext}
              gainNode={gainNode1}
              isPlaying={isPlaying}
              currentTime={currentTime}
              zoom={zoom}
              onPlay={play}
              onPause={pause}
              onStop={stop}
              onSeek={handleSeek}
              onDelete={handleSynchronizedDelete}
              timeOffset={timeOffset1}
              onTimeOffsetChange={setTimeOffset1}
            />
          )}
        </div>
        <div style={{ width: `${100 * zoom}%` }}>
          {buffer2 && (
            <SingleAudioEditor
              audioBuffer={buffer2}
              setAudioBuffer={setBuffer2}
              audioContext={audioContext}
              gainNode={gainNode2}
              isPlaying={isPlaying}
              currentTime={currentTime}
              zoom={zoom}
              onPlay={play}
              onPause={pause}
              onStop={stop}
              onSeek={handleSeek}
              onDelete={handleSynchronizedDelete}
              timeOffset={timeOffset2}
              onTimeOffsetChange={setTimeOffset2}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiAudioEditor;
